import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IntercompanyDecisionCodeRequest, IntercompanyPreviewGroup } from 'returnCenter-api';
import { intercompanyService } from 'services/intercompanyService';
import type { RootState } from 'store';

type InitialState = {
    preview: IntercompanyPreviewGroup[];
    loading: boolean;
    failed: boolean;

    /**
     * Suggestions resolved live as the operator edits. Kept apart from the preview rather than
     * merged into it, which would lose the prices and warnings that came with it.
     */
    liveSuggestions: Record<number, number | null>;

    /** What the supplying company ends up seeing per row, and why it will not do. */
    liveReasonCodes: Record<number, { effective: number; problem: string | null }>;
};

const initialState: InitialState = {
    preview: [],
    loading: false,
    failed: false,
    liveSuggestions: {},
    liveReasonCodes: {},
};

/**
 * Kept in the store rather than in the panel because completing the return has to see it: a line
 * the supplying company cannot credit must stop the operator, and that check lives in
 * useReturnSlipActions.
 */
export const fetchIntercompanyPreview = createAsyncThunk(
    'intercompany/fetchPreview',
    async (returnId: number) => intercompanyService.getPreview(returnId),
);

/** Re-asks the supplying companies as the operator edits. No ERP behind it — see the service. */
export const resolveIntercompanyDecisionCodes = createAsyncThunk(
    'intercompany/resolveDecisionCodes',
    async (request: IntercompanyDecisionCodeRequest) => intercompanyService.resolveDecisionCodes(request),
);

const intercompanySlice = createSlice({
    name: 'intercompany',
    initialState,
    reducers: {
        clearIntercompanyPreview: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchIntercompanyPreview.pending, (state) => {
            state.loading = true;
            state.failed = false;
        });
        builder.addCase(
            fetchIntercompanyPreview.fulfilled,
            (state, action: PayloadAction<IntercompanyPreviewGroup[]>) => {
                state.preview = action.payload;
                state.loading = false;
                state.failed = false;
                // The preview is the fresher answer for every row it covers.
                state.liveSuggestions = {};
                state.liveReasonCodes = {};
            },
        );
        builder.addCase(resolveIntercompanyDecisionCodes.fulfilled, (state, action) => {
            for (const result of action.payload) {
                if (result.returnItemRowId != null) {
                    state.liveSuggestions[result.returnItemRowId] = result.decisionCodeId ?? null;
                    state.liveReasonCodes[result.returnItemRowId] = {
                        effective: result.effectiveReasonCode ?? 0,
                        problem: result.reasonCodeProblem ?? null,
                    };
                }
            }
        });
        builder.addCase(fetchIntercompanyPreview.rejected, (state) => {
            // Prices are informational, so a failed fetch must not block the operator — but it
            // also means selectProblems can only report problems it actually saw.
            state.preview = [];
            state.loading = false;
            state.failed = true;
            state.liveReasonCodes = {};
        });
    },
});

export const { clearIntercompanyPreview } = intercompanySlice.actions;

/**
 * Lines the supplying company cannot credit as things stand. Memoized because it builds a new array
 * and useReturnSlipActions reads it on every render.
 *
 * A line still waiting for a reason code is not one of them: that is every fresh row, and counting
 * it would make the warning meaningless the day it matters.
 */
const selectProblems = createSelector(
    [(state: RootState) => state.intercompany.preview, (state: RootState) => state.intercompany.liveReasonCodes],
    (preview, liveReasonCodes) =>
        preview.flatMap((group) =>
            (group.lines ?? [])
                .map((line) => {
                    // The live answer supersedes the preview's, so a fix unblocks completion at
                    // once rather than after a reload.
                    const live = line.returnItemRowId != null ? liveReasonCodes[line.returnItemRowId] : undefined;
                    const reasonProblem = live ? live.problem : (line.reasonCodeProblem ?? null);

                    return {
                        supplyingCompany: group.supplyingCompany ?? '',
                        sku: line.sku ?? '',
                        problem: line.pricingProblem ?? reasonProblem ?? line.decisionCodeProblem ?? '',
                    };
                })
                .filter((entry) => entry.problem !== ''),
        ),
);

export const intercompanySelectors = {
    selectPreview: (state: RootState) => state.intercompany.preview,
    selectFailed: (state: RootState) => state.intercompany.failed,
    selectLiveSuggestions: (state: RootState) => state.intercompany.liveSuggestions,
    selectLiveReasonCodes: (state: RootState) => state.intercompany.liveReasonCodes,

    selectProblems,
};

export default intercompanySlice.reducer;
